import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  CompoundEntityRef,
  DEFAULT_NAMESPACE,
  Entity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/frontend-plugin-api';
import {
  catalogApiRef,
  humanizeEntityRef,
} from '@backstage/plugin-catalog-react';

type EntityProfile = {
  displayName?: string;
  email?: string;
};

type EntitySpec = {
  profile?: EntityProfile;
};

const entitySpec = (entity: Entity) => entity.spec as EntitySpec | undefined;

export const titleCaseEntityName = (value: string) =>
  value
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

export const entityDisplayName = (entity: Entity) =>
  entity.metadata.title ??
  entitySpec(entity)?.profile?.displayName ??
  entitySpec(entity)?.profile?.email ??
  titleCaseEntityName(entity.metadata.name);

export const emailFromUserName = (name: string) => {
  if (name.toLowerCase() === 'guest') {
    return 'guest@example.com';
  }

  if (/_scania\.com$/i.test(name)) {
    return name.replace(/_scania\.com$/i, '@scania.com');
  }

  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(name)) {
    return name;
  }

  return undefined;
};

export const entityUserEmail = (
  entityOrRelation?: Entity | CompoundEntityRef,
) => {
  if (!entityOrRelation) {
    return undefined;
  }

  if ('metadata' in entityOrRelation) {
    if (entityOrRelation.kind.toLowerCase() !== 'user') {
      return undefined;
    }

    return (
      entitySpec(entityOrRelation)?.profile?.email ??
      emailFromUserName(entityOrRelation.metadata.name)
    );
  }

  if (entityOrRelation.kind.toLowerCase() !== 'user') {
    return undefined;
  }

  return emailFromUserName(entityOrRelation.name);
};

export const relationEntityRef = (relation: CompoundEntityRef) =>
  stringifyEntityRef({
    kind: relation.kind,
    namespace: relation.namespace ?? DEFAULT_NAMESPACE,
    name: relation.name,
  });

export const humanizedRelationLabel = (
  relation: CompoundEntityRef,
  defaultKind: string,
) =>
  humanizeEntityRef(relation, {
    defaultKind,
    defaultNamespace: DEFAULT_NAMESPACE,
  });

export const useResolvedEntityLabels = (
  relations: CompoundEntityRef[],
  fallbackLabel: (relation: CompoundEntityRef) => string,
) => {
  const catalogApi = useApi(catalogApiRef);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const refsKey = useMemo(
    () =>
      Array.from(new Set(relations.map(relationEntityRef)))
        .sort()
        .join('|'),
    [relations],
  );

  useEffect(() => {
    let active = true;
    const entityRefs = refsKey ? refsKey.split('|') : [];

    if (!entityRefs.length) {
      setLabels({});
      return () => {
        active = false;
      };
    }

    const loadLabels = async () => {
      try {
        const response = await catalogApi.getEntitiesByRefs({ entityRefs });

        if (!active) {
          return;
        }

        setLabels(
          Object.fromEntries(
            response.items
              .map((entity, index) =>
                entity ? [entityRefs[index], entityDisplayName(entity)] : null,
              )
              .filter((item): item is [string, string] => Boolean(item)),
          ),
        );
      } catch {
        if (active) {
          setLabels({});
        }
      }
    };

    void loadLabels();

    return () => {
      active = false;
    };
  }, [catalogApi, refsKey]);

  return useCallback(
    (relation: CompoundEntityRef) =>
      labels[relationEntityRef(relation)] ?? fallbackLabel(relation),
    [fallbackLabel, labels],
  );
};
